import { supabase } from '../config/supabase';

/**
 * Fetch client activity (new clients, new/updated products, new Amazon Registry submissions) that the given admin has not yet "seen".
 * @param {string} userId - auth user id of the admin
 * @returns {Promise<{ newClients: Array<{ id, label, createdAt }>, newProducts: Array<{ id, clientId, clientName, label, createdAt }>, newAmazonRegistry: Array<{ id, clientId, clientName, createdAt }> }>}
 */
export async function fetchNewActivity(userId) {
  if (!userId) return { newClients: [], newProducts: [], newAmazonRegistry: [] };

  const { data: seenRows } = await supabase
    .from('admin_seen_activity')
    .select('entity_type, entity_id')
    .eq('user_id', userId);

  const seenProfiles = new Set(
    (seenRows || []).filter((r) => r.entity_type === 'client_profile').map((r) => r.entity_id)
  );
  const seenProducts = new Set(
    (seenRows || []).filter((r) => r.entity_type === 'client_product').map((r) => r.entity_id)
  );
  const seenRegistries = new Set(
    (seenRows || []).filter((r) => r.entity_type === 'amazon_registry').map((r) => r.entity_id)
  );

  const { data: profiles } = await supabase
    .from('client_profiles')
    .select('id, company_name, setup_completed_at, created_at')
    .not('setup_completed_at', 'is', null)
    .order('setup_completed_at', { ascending: false });

  const newClients = (profiles || [])
    .filter((p) => !seenProfiles.has(p.id))
    .map((p) => ({
      id: p.id,
      label: p.company_name || 'New client',
      createdAt: p.setup_completed_at || p.created_at,
    }));

  const { data: products } = await supabase
    .from('client_products')
    .select('id, client_profile_id, product_name, sku_number, status, created_at, updated_at')
    .eq('status', 'submitted')
    .order('updated_at', { ascending: false });

  const profileIds = [...new Set((products || []).map((p) => p.client_profile_id))];
  const { data: profileNames } = await supabase
    .from('client_profiles')
    .select('id, company_name')
    .in('id', profileIds.length ? profileIds : ['00000000-0000-0000-0000-000000000000']);

  const nameByProfile = Object.fromEntries((profileNames || []).map((p) => [p.id, p.company_name || 'Client']));

  const newProducts = (products || [])
    .filter((p) => !seenProducts.has(p.id))
    .map((p) => ({
      id: p.id,
      clientId: p.client_profile_id,
      clientName: nameByProfile[p.client_profile_id] || 'Client',
      label: p.product_name || p.sku_number || 'Product',
      createdAt: p.updated_at || p.created_at,
    }));

  const { data: registries } = await supabase
    .from('amazon_brand_registry')
    .select('id, client_profile_id, submitted_at')
    .eq('status', 'submitted')
    .not('submitted_at', 'is', null)
    .order('submitted_at', { ascending: false });

  const registryProfileIds = [...new Set((registries || []).map((r) => r.client_profile_id))];
  const { data: registryProfileNames } = await supabase
    .from('client_profiles')
    .select('id, company_name')
    .in('id', registryProfileIds.length ? registryProfileIds : ['00000000-0000-0000-0000-000000000000']);

  const registryNameByProfile = Object.fromEntries(
    (registryProfileNames || []).map((p) => [p.id, p.company_name || 'Client'])
  );

  const newAmazonRegistry = (registries || [])
    .filter((r) => !seenRegistries.has(r.id))
    .map((r) => ({
      id: r.id,
      clientId: r.client_profile_id,
      clientName: registryNameByProfile[r.client_profile_id] || 'Client',
      createdAt: r.submitted_at,
    }));

  return { newClients, newProducts, newAmazonRegistry };
}

/**
 * Mark an entity as seen by the given admin. Removes the "New" badge and prevents it reappearing.
 * @param {string} userId - auth user id of the admin
 * @param {'client_profile'|'client_product'} entityType
 * @param {string} entityId - uuid of the profile or product
 */
export async function markActivityAsSeen(userId, entityType, entityId) {
  if (!userId || !entityType || !entityId) return;
  await supabase.from('admin_seen_activity').upsert(
    {
      user_id: userId,
      entity_type: entityType,
      entity_id: entityId,
      seen_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,entity_type,entity_id' }
  );
}
